/**
 * Validation Schemas (Zod)
 * ========================
 * These schemas are the CONTRACT for all data entering the system.
 * They run on BOTH the client (UX feedback) and server (Cloud Functions).
 *
 * Security model: client validation = nice UX. Server validation = real security.
 * An attacker can bypass client-side checks with DevTools. They cannot bypass
 * Cloud Function validation.
 *
 * Rules:
 *  - All text fields: trimmed, max length enforced, HTML stripped
 *  - All enum fields: use z.enum() — no arbitrary string values
 *  - All numeric fields: min/max bounds enforced
 *  - No field is optional unless truly optional in the domain
 */

import { z } from "zod";

// ─── REUSABLE PRIMITIVES ─────────────────────────────────────────────────────
/** Safe text — trimmed, no HTML, sensible max length */
const safeText = (maxLen: number, label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(maxLen, `${label} must be ${maxLen} characters or fewer`)
    .refine(
      (val) => !/<[^>]*>/.test(val),
      `${label} must not contain HTML`
    );

/** Safe optional text */
const safeTextOptional = (maxLen: number) =>
  z
    .string()
    .trim()
    .max(maxLen, `Must be ${maxLen} characters or fewer`)
    .refine((val) => !/<[^>]*>/.test(val), "Must not contain HTML")
    .optional();

// ─── ENUMS ────────────────────────────────────────────────────────────────────
export const BookCategorySchema = z.enum([
  "fantasy", "adventure", "science", "stories",
  "comics", "school", "nature", "other",
]);

export const BookConditionSchema = z.enum([
  "like-new", "good", "fair", "worn",
]);

export const DeliveryTypeSchema = z.enum([
  "meetup", "deliver", "both",
]);

export const DurationUnitSchema = z.enum([
  "days", "weeks", "months",
]);

export const ReminderMethodSchema = z.enum([
  "email", "sms", "push", "whatsapp",
]);

// ─── ADD BOOK FORM ────────────────────────────────────────────────────────────
export const AddBookSchema = z.object({
  title:       safeText(120, "Title"),
  author:      safeText(80,  "Author"),
  description: safeText(500, "Description"),
  category:    BookCategorySchema,
  condition:   BookConditionSchema,

  tags: z
    .array(safeText(30, "Tag"))
    .max(10, "Maximum 10 tags")
    .default([]),

  durationValue: z
    .number()
    .int()
    .min(1, "Duration must be at least 1")
    .max(52, "Maximum 52 weeks (1 year)"),

  durationUnit: DurationUnitSchema,

  deliveryType:   DeliveryTypeSchema,
  allowExtension: z.boolean().default(true),

  reminderMethods: z
    .array(ReminderMethodSchema)
    .min(1, "Select at least one reminder method"),

  mediaIds: z
    .array(z.string().uuid("Invalid media ID"))
    .min(1, "At least one photo or video is required")
    .max(8, "Maximum 8 media files"),
});

export type AddBookInput = z.infer<typeof AddBookSchema>;

// ─── BORROW REQUEST FORM ─────────────────────────────────────────────────────
export const BorrowRequestSchema = z.object({
  bookId: z.string().min(1, "Book ID is required"),

  requestedWeeks: z
    .number()
    .int()
    .min(1)
    .max(52),

  deliveryType: z.enum(["meetup", "deliver"]),

  deliveryAddress: z
    .string()
    .trim()
    .max(200)
    .optional(),

  message: safeTextOptional(300),

  reminderMethods: z
    .array(ReminderMethodSchema)
    .min(1, "Select at least one reminder method"),
}).refine(
  (data) =>
    data.deliveryType !== "deliver" || Boolean(data.deliveryAddress?.trim()),
  {
    message: "Delivery address is required for home delivery",
    path: ["deliveryAddress"],
  }
);

export type BorrowRequestInput = z.infer<typeof BorrowRequestSchema>;

// ─── USER PROFILE ─────────────────────────────────────────────────────────────
export const UpdateProfileSchema = z.object({
  displayName: safeText(60, "Display name"),
  phoneNumber: z
    .string()
    .regex(/^\+?[1-9]\d{7,14}$/, "Invalid phone number")
    .optional()
    .or(z.literal("")),
  reminderPreferences: z.array(ReminderMethodSchema),
});

export type UpdateProfileInput = z.infer<typeof UpdateProfileSchema>;

// ─── REVIEW SCHEMA ────────────────────────────────────────────────────────────
export const ReviewSchema = z.object({
  requestId: z.string().min(1),
  rating: z
    .number()
    .int()
    .min(1, "Rating must be at least 1 star")
    .max(5, "Rating cannot exceed 5 stars"),
  text: safeTextOptional(600),
});

export type ReviewInput = z.infer<typeof ReviewSchema>;

// ─── SEARCH / FILTER ─────────────────────────────────────────────────────────
export const BookFiltersSchema = z.object({
  category:     z.union([BookCategorySchema, z.literal("all")]).default("all"),
  searchQuery:  z.string().trim().max(100).default(""),
  nearbyOnly:   z.boolean().default(true),
  deliveryType: z.union([DeliveryTypeSchema, z.literal("all")]).default("all"),
  sortBy:       z.enum(["distance", "rating", "newest", "title"]).default("distance"),
});

export type BookFiltersInput = z.infer<typeof BookFiltersSchema>;

// ─── MEDIA UPLOAD ─────────────────────────────────────────────────────────────
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"] as const;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB
const MAX_VIDEO_BYTES = 50 * 1024 * 1024; // 50 MB

export const MediaFileSchema = z
  .instanceof(File)
  .refine(
    (f) =>
      ([...ALLOWED_IMAGE_TYPES, ...ALLOWED_VIDEO_TYPES] as string[]).includes(f.type),
    "File must be JPG, PNG, WEBP, MP4, WEBM, or MOV"
  )
  .refine((f) => {
    const isVideo = (ALLOWED_VIDEO_TYPES as readonly string[]).includes(f.type);
    return f.size <= (isVideo ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES);
  }, "File exceeds maximum size");

export const MediaBatchSchema = z
  .array(MediaFileSchema)
  .min(1, "At least one file required")
  .max(8, "Maximum 8 files");
